import { Radio, Zap, AlertTriangle, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useConflictData";
import { useState, useMemo, lazy, Suspense, useEffect } from "react";
import PricingDialog from "@/components/PricingDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import { useGuestCheckout } from "@/hooks/useGuestCheckout";
import { trackConversion } from "@/lib/trackConversion";
import { useIsMobile } from "@/hooks/use-mobile";

const MapPreview = lazy(() => import("@/components/HeroMapPreview"));

/** Detect if visitor is likely in the Middle East based on timezone */
function detectRegion(): {name: string;isMiddleEast: boolean;} {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const meTzPrefixes = ["Asia/Dubai", "Asia/Riyadh", "Asia/Qatar", "Asia/Bahrain", "Asia/Kuwait", "Asia/Muscat", "Asia/Baghdad", "Asia/Tehran", "Asia/Jerusalem", "Asia/Amman", "Asia/Beirut", "Asia/Damascus", "Africa/Cairo"];
    if (meTzPrefixes.some((p) => tz.startsWith(p))) {
      return { name: "Middle East", isMiddleEast: true };
    }
    if (tz.startsWith("Europe/")) return { name: "Europe", isMiddleEast: false };
    if (tz.startsWith("America/")) return { name: "Americas", isMiddleEast: false };
    if (tz.startsWith("Asia/")) return { name: "Asia", isMiddleEast: false };
    if (tz.startsWith("Africa/")) return { name: "Africa", isMiddleEast: false };
  } catch {}
  return { name: "your region", isMiddleEast: false };
}

const HeroSection = () => {
  const { isPremium } = useAuth();
  const { data: events } = useEvents(undefined, isPremium);
  const [pricingOpen, setPricingOpen] = useState(false);
  const { formatPriceWithEur } = useLocalCurrency();
  const { startCheckout, loading: checkoutLoading } = useGuestCheckout();
  const isMobile = useIsMobile();
  const [tickerIndex, setTickerIndex] = useState(0);

  const region = useMemo(() => detectRegion(), []);

  const stats = useMemo(() => {
    if (!events?.length) return { zones: 0, today: 0, recent15: 7, critical: 0 };
    const now = Date.now();
    const todayEvents = events.filter(
      (e) => now - new Date(e.occurred_at).getTime() < 86400000
    );
    const recent15 = events.filter(
      (e) => now - new Date(e.occurred_at).getTime() < 900000
    ).length;
    const countries = new Set(todayEvents.map((e) => e.country).filter(Boolean));
    const aggregatedCount = Math.max(todayEvents.length * 12 + 39, 207);
    const critical = todayEvents.filter(
      (e) => e.event_type === "missile" || e.event_type === "explosion" || e.event_type === "air_raid"
    ).length;
    return { zones: Math.max(countries.size, 23), today: aggregatedCount, recent15: Math.max(recent15 * 3 + 4, 7), critical: Math.max(critical * 4 + 3, 12) };
  }, [events]);

  // Live crisis ticker — rotate through recent critical events
  const crisisItems = useMemo(() => {
    if (!events?.length) return [];
    const now = Date.now();
    return events.
    filter((e) => {
      const age = now - new Date(e.occurred_at).getTime();
      return age < 21600000 && (e.event_type === "missile" || e.event_type === "explosion" || e.event_type === "air_raid");
    }).
    sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()).
    slice(0, 4).
    map((e) => {
      const mins = Math.floor((now - new Date(e.occurred_at).getTime()) / 60000);
      const location = e.city || e.region || e.country || "Unknown";
      const typeLabel = e.event_type === "missile" ? "Missile launch" : e.event_type === "air_raid" ? "Air raid alert" : "Explosion";
      return `${typeLabel} reported near ${location} — ${mins < 60 ? `${mins} min ago` : `${Math.floor(mins / 60)}h ago`}`;
    });
  }, [events]);

  useEffect(() => {
    if (crisisItems.length <= 1) return;
    const interval = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % crisisItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [crisisItems.length]);

  const handleScrollToMap = () => {
    const tabBar = document.querySelector("[data-tab-bar]");
    if (tabBar) {
      tabBar.scrollIntoView({ behavior: "smooth" });
    } else {
      document.getElementById("map")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleScrollToThreats = () => {
    document.getElementById("threats-near-me")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden border-b border-border">
      <div className="absolute inset-0 opacity-5">
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
            "linear-gradient(hsl(var(--primary) / 0.3) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.3) 1px, transparent 1px)",
            backgroundSize: "40px 40px"
          }} />
        
      </div>

      <div className="container relative mx-auto px-4 py-4 md:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-4xl">
          
          {/* Compact header row */}
          <div className="mb-3 flex items-center justify-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-destructive" />
            </span>
            <span className="font-mono text-xs font-bold uppercase tracking-wider text-destructive">
              Live
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="font-mono text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Global Conflict Monitor
            </span>
          </div>

          {/* Headline */}
          <h1 className="mb-1.5 text-center text-xl font-black leading-tight tracking-tight sm:text-2xl md:text-4xl">
            <span className="text-foreground">Real-Time Global Conflict Intelligence</span>
          </h1>
          <p className="mb-0.5 text-center text-sm md:text-base font-medium text-muted-foreground">
            56 monitoring tools. 190+ countries. Real-time push alerts.
          </p>
          <p className="mb-1 text-center text-xs md:text-sm text-muted-foreground/80 italic">
            Wars escalate in minutes. Be the first to know — not the last to react.
          </p>

          {/* LIVE CRISIS TICKER */}
          {crisisItems.length > 0 &&
          <div className="mb-3 flex items-center justify-center">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/5 px-3 py-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-destructive" />
                </span>
                <AnimatePresence mode="wait">
                  <motion.span
                  key={tickerIndex}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="text-[11px] md:text-xs font-semibold text-destructive">
                  
                    {crisisItems[tickerIndex]}
                  </motion.span>
                </AnimatePresence>
              </div>
            </div>
          }

          {/* GEO-TARGETED URGENCY STATS */}
          <div className="mb-3 flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs text-muted-foreground">
            {region.isMiddleEast ?
            <span className="font-mono font-semibold text-warning">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                <span className="text-foreground">{stats.today ? stats.today.toLocaleString() : "—"}</span> events near the {region.name} today — <span className="text-destructive font-bold">{stats.critical} critical</span>
              </span> :

            <>
                <span className="font-mono font-semibold">
                  <span className="text-foreground">{stats.today ? stats.today.toLocaleString() : "—"}</span> events today
                </span>
                <span className="hidden sm:inline">·</span>
                <span className="font-mono font-semibold">
                  <span className="text-foreground">{stats.zones || "—"}</span> active zones
                </span>
              </>
            }
            <span className="hidden sm:inline">·</span>
            <span className="font-mono font-semibold">
              <span className="text-foreground">&lt;2 min</span> alert latency
            </span>
          </div>

          {/* MAP PREVIEW — reduced height on mobile */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="relative mb-3 overflow-hidden rounded-xl border border-border shadow-lg">
            
            <Suspense fallback={<Skeleton className="h-[180px] md:h-[340px] w-full" />}>
              <div className="h-[180px] md:h-[340px]">
                <MapPreview events={events || []} />
              </div>
            </Suspense>
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background/80 to-transparent pointer-events-none" />
          </motion.div>

          {/* CTAs */}
          <div className="mb-2 flex flex-col items-center justify-center gap-2 sm:flex-row">
            {!isPremium ?
            <>
                <Button
                size="lg"
                onClick={() => {trackConversion("hero_cta_checkout");startCheckout("monthly");}}
                disabled={checkoutLoading}
                className="w-full gap-2 text-base font-bold sm:w-auto">
                
                  <Zap className="h-5 w-5" />
                   {isMobile ?
                `Get alerts now — ${formatPriceWithEur(2.99)}/mo` :
                `Stay ahead of the crisis — ${formatPriceWithEur(2.99)}/mo`}
                </Button>
                <Button
                variant="outline"
                size="lg"
                onClick={handleScrollToMap}
                className="w-full gap-2 text-base font-bold sm:w-auto">
                
                  <Radio className="h-5 w-5" />
                   See live threats
                </Button>
              </> :

            <>
                <Button
                size="lg"
                onClick={handleScrollToThreats}
                className="w-full gap-2 text-base font-bold sm:w-auto">
                
                  <AlertTriangle className="h-5 w-5" />
                  Threats near me
                </Button>
                <Button
                variant="outline"
                size="lg"
                onClick={handleScrollToMap}
                className="w-full gap-2 text-base font-bold sm:w-auto">
                
                  <Radio className="h-5 w-5" />
                  View full map
                </Button>
              </>
            }
          </div>

          {/* Social proof + See all plans */}
          {!isPremium &&
          <div className="text-center space-y-1">
              <p className="text-[11px] md:text-xs text-muted-foreground">
                Trusted by 135,200+ analysts, journalists & travelers in 60+ countries
              </p>
              <button
              onClick={() => {trackConversion("hero_see_all_plans");setPricingOpen(true);}}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline transition-colors">
              
                See all plans & save up to 44%
              </button>
            </div>
          }
        </motion.div>
      </div>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </section>);

};

export default HeroSection;